const BADGE_PRICE_CENTS = 25;
const MINIMUM_STRIPE_USD_CHARGE_CENTS = 50;
const FREE_ATTENDEE_RATE = 0.05;
const PAYMENT_ATTENDEE_FLAGS_KEY = "paymentAttendeeFlags";

const normalizeValue = (value) =>
  String(value ?? "")
    .replace(/\u00a0/g, " ")
    .trim();

const getSpreadsheetData = (payment) =>
  payment?.spreadsheetData ?? payment?.spreadsheet_data ?? null;

const getFieldMapping = (payment) =>
  payment?.fieldMapping ??
  payment?.field_mapping ??
  getSpreadsheetData(payment)?.mapping ??
  {};

const getChargedAttendeeCount = (payment) => {
  const explicitCount =
    payment?.chargedAttendeeCount ?? payment?.charged_attendee_count;

  if (Number.isFinite(Number(explicitCount))) {
    return Math.max(0, Number(explicitCount));
  }

  const amountCents = payment?.amountCents ?? payment?.amount_cents;
  return Math.max(0, Math.round(Number(amountCents || 0) / BADGE_PRICE_CENTS));
};

const getBadgeCreditCount = (payment, camelCaseKey, snakeCaseKey) => {
  const value = payment?.[camelCaseKey] ?? payment?.[snakeCaseKey];
  return Number.isFinite(Number(value)) ? Math.max(0, Number(value)) : 0;
};

const spreadsheetDataToAttendees = (spreadsheetData, fieldMapping) => {
  const fileData = spreadsheetData?.fileData || {};
  const headers = fileData.headers || [];
  const mapping = fieldMapping || spreadsheetData?.mapping || {};

  return (fileData.data || []).map((row) =>
    Object.entries(mapping).reduce((attendee, [header, fieldName]) => {
      const columnIndex = headers.indexOf(header);
      if (columnIndex !== -1 && fieldName) {
        attendee[fieldName] = normalizeValue(row?.[columnIndex]);
      }
      return attendee;
    }, {})
  );
};

const attendeesMatch = (paidAttendee, currentAttendee) =>
  Object.keys(paidAttendee).every(
    (fieldName) =>
      normalizeValue(currentAttendee?.[fieldName]) ===
      normalizeValue(paidAttendee[fieldName])
  );

const findUnmatchedAttendeeIndexes = (currentAttendees, paidAttendees) => {
  const availablePaidIndexes = new Set(paidAttendees.map((_, index) => index));
  const unmatchedIndexes = [];

  currentAttendees.forEach((attendee, currentIndex) => {
    const paidIndex = Array.from(availablePaidIndexes).find((candidateIndex) =>
      attendeesMatch(paidAttendees[candidateIndex], attendee)
    );

    if (paidIndex === undefined) {
      unmatchedIndexes.push(currentIndex);
    } else {
      availablePaidIndexes.delete(paidIndex);
    }
  });

  return unmatchedIndexes;
};

const countUsedFreeAttendees = (payments) =>
  payments.reduce((total, payment) => {
    const flags = getSpreadsheetData(payment)?.[PAYMENT_ATTENDEE_FLAGS_KEY] || [];
    return total + flags.filter((flag) => flag?.freeAttendee === true).length;
  }, 0);

const calculatePaymentQuote = ({
  spreadsheetData,
  fieldMapping,
  payments = [],
  creditPayments = payments,
}) => {
  const completedPayments = payments.filter(
    (payment) => !payment?.status || payment.status === "paid"
  );
  const completedCreditPayments = creditPayments.filter(
    (payment) => !payment?.status || payment.status === "paid"
  );
  const currentAttendees = spreadsheetDataToAttendees(
    spreadsheetData,
    fieldMapping || spreadsheetData?.mapping
  );
  const latestPayment = completedPayments[0] || null;
  const paidAttendees = latestPayment
    ? spreadsheetDataToAttendees(
        getSpreadsheetData(latestPayment),
        getFieldMapping(latestPayment)
      )
    : [];
  const unmatchedAttendeeIndexes = latestPayment
    ? findUnmatchedAttendeeIndexes(currentAttendees, paidAttendees)
    : currentAttendees.map((_, index) => index);
  const paidAttendeeCount = completedPayments.reduce(
    (total, payment) => total + getChargedAttendeeCount(payment),
    0
  );
  const usedFreeAttendeeCount = countUsedFreeAttendees(completedPayments);
  const totalFreeAttendeeAllowance = Math.floor(
    paidAttendeeCount * FREE_ATTENDEE_RATE
  );
  const availableFreeAttendeeCount = Math.max(
    0,
    totalFreeAttendeeAllowance - usedFreeAttendeeCount
  );
  const freeAttendeeIndexes = unmatchedAttendeeIndexes.slice(
    0,
    availableFreeAttendeeCount
  );
  const billableAttendeeIndexes = unmatchedAttendeeIndexes.slice(
    availableFreeAttendeeCount
  );
  const purchasedBadgeCreditCount = completedCreditPayments.reduce(
    (total, payment) =>
      total +
      getBadgeCreditCount(
        payment,
        "badgeCreditPurchasedCount",
        "badge_credit_purchased_count"
      ),
    0
  );
  const usedBadgeCreditCount = completedCreditPayments.reduce(
    (total, payment) =>
      total +
      getBadgeCreditCount(
        payment,
        "badgeCreditUsedCount",
        "badge_credit_used_count"
      ),
    0
  );
  const availableBadgeCreditCount = Math.max(
    0,
    purchasedBadgeCreditCount - usedBadgeCreditCount
  );
  const badgeCreditUsedCount = Math.min(
    availableBadgeCreditCount,
    billableAttendeeIndexes.length
  );
  const uncoveredAttendeeCount =
    billableAttendeeIndexes.length - badgeCreditUsedCount;
  const amountBeforeMinimumCents = uncoveredAttendeeCount * BADGE_PRICE_CENTS;
  const badgeCreditPurchasedCount =
    amountBeforeMinimumCents > 0 &&
    amountBeforeMinimumCents < MINIMUM_STRIPE_USD_CHARGE_CENTS
      ? Math.ceil(
          (MINIMUM_STRIPE_USD_CHARGE_CENTS - amountBeforeMinimumCents) /
            BADGE_PRICE_CENTS
        )
      : 0;
  const amountCents =
    amountBeforeMinimumCents +
    badgeCreditPurchasedCount * BADGE_PRICE_CENTS;
  const remainingFreeAttendeeCountAfterPurchase = Math.max(
    0,
    Math.floor(
      (paidAttendeeCount + billableAttendeeIndexes.length) *
        FREE_ATTENDEE_RATE
    ) -
      usedFreeAttendeeCount
  );

  return {
    amountBeforeMinimumCents,
    amountCents,
    attendeeCount: currentAttendees.length,
    availableFreeAttendeeCount,
    availableBadgeCreditCount,
    badgeCreditPurchasedCount,
    badgeCreditUsedCount,
    badgePriceCents: billableAttendeeIndexes.length * BADGE_PRICE_CENTS,
    billableAttendeeCount: billableAttendeeIndexes.length,
    billableAttendeeIndexes,
    freeAttendeeIndexes,
    paidAttendeeCount,
    remainingFreeAttendeeCountAfterPurchase,
    unitPriceCents: BADGE_PRICE_CENTS,
    remainingBadgeCreditCount:
      availableBadgeCreditCount -
      badgeCreditUsedCount +
      badgeCreditPurchasedCount,
    unmatchedAttendeeCount: unmatchedAttendeeIndexes.length,
    unmatchedAttendeeIndexes,
    usedFreeAttendeeCount,
  };
};

const addPaymentAttendeeFlags = (spreadsheetData, freeAttendeeIndexes = []) => {
  const freeIndexes = new Set(freeAttendeeIndexes);
  const attendeeCount = spreadsheetData?.fileData?.data?.length || 0;

  return {
    ...spreadsheetData,
    [PAYMENT_ATTENDEE_FLAGS_KEY]: Array.from(
      { length: attendeeCount },
      (_, index) => ({ freeAttendee: freeIndexes.has(index) })
    ),
  };
};

module.exports = {
  BADGE_PRICE_CENTS,
  MINIMUM_STRIPE_USD_CHARGE_CENTS,
  FREE_ATTENDEE_RATE,
  PAYMENT_ATTENDEE_FLAGS_KEY,
  addPaymentAttendeeFlags,
  attendeesMatch,
  calculatePaymentQuote,
  findUnmatchedAttendeeIndexes,
  spreadsheetDataToAttendees,
};
